import { useState, useEffect, useCallback } from "react";
import "./App.css";
import { C, ARTICLES } from "./lib/constants";
import { AuthProvider, useAuth } from "./lib/AuthContext";
import { AuthModal, AuthCallback } from "./lib/Auth";
import { cartApi } from "./lib/api";
import { AnnouncementBar, Navbar, Footer, CartDrawer } from "./components/Layout";
import HomePage from "./pages/HomePage";
import ProductPage from "./pages/ProductPage";
import { JournalPage, ArticlePage } from "./pages/Journal";
import { StoryPage, FAQPage, CommunityPage, AccountPage, SizeGuidePage } from "./pages/Misc";
import { PrivacyPage, TermsPage } from "./pages/Legal";
import TrackerAppPage from "./pages/TrackerApp";

const KNOWN = ["home", "product", "tracker", "community", "journal", "story", "faq", "account", "sizeguide", "privacy", "terms"];
const DEFAULT_SLUG = "why-period-rest-is-a-form-of-productivity";

function parseRoute(pathname) {
  const p = pathname.replace(/^\/+|\/+$/g, "");
  if (p.startsWith("journal/")) {
    const slug = p.slice(8);
    return { page: "article", slug: ARTICLES.some(a => a.slug === slug) ? slug : DEFAULT_SLUG };
  }
  if (p === "article") return { page: "article", slug: DEFAULT_SLUG };
  return { page: KNOWN.includes(p) ? p : "home", slug: null };
}

const routePath = (r) => r.page === "article" ? `/journal/${r.slug}` : r.page === "home" ? "/" : `/${r.page}`;

export function Shell() {
  const { user, loading, openAuth, logout } = useAuth();
  const [route, setRoute] = useState(() => parseRoute(window.location.pathname));
  const page = route.page;
  const [cartOpen, setCartOpen] = useState(false);
  const [items, setItems] = useState([]);

  useEffect(() => {
    const r0 = parseRoute(window.location.pathname);
    if (r0.page === "article" && window.location.pathname !== routePath(r0)) {
      window.history.replaceState({}, "", routePath(r0));
    }
    const onPop = () => setRoute(parseRoute(window.location.pathname));
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);

  // Persist cart: load when user changes, save on each update
  useEffect(() => {
    (async () => {
      if (user) {
        try {
          const { data } = await cartApi.get();
          setItems(data.items || []);
        } catch { /* ignore */ }
      } else {
        // Load guest cart from localStorage
        try {
          const raw = localStorage.getItem("4ever_guest_cart");
          setItems(raw ? JSON.parse(raw) : []);
        } catch { setItems([]); }
      }
    })();
  }, [user]);

  const syncCart = useCallback(async (next) => {
    if (user) {
      try { await cartApi.set(next); } catch {}
    } else {
      try { localStorage.setItem("4ever_guest_cart", JSON.stringify(next)); } catch {}
    }
  }, [user]);

  const setItemsAndSync = useCallback((next) => {
    setItems(next);
    syncCart(next);
  }, [syncCart]);

  // Listen for auth-open events from deep components (e.g., cart when 401)
  useEffect(() => {
    const h = () => openAuth();
    window.addEventListener("4ever:open-auth", h);
    return () => window.removeEventListener("4ever:open-auth", h);
  }, [openAuth]);

  // Listen for cart-open events (e.g. Reorder in order history) — refetch the
  // server cart and open the drawer.
  useEffect(() => {
    const h = async () => {
      if (user) {
        try {
          const { data } = await cartApi.get();
          setItems(data.items || []);
        } catch { /* ignore */ }
      }
      setCartOpen(true);
    };
    window.addEventListener("4ever:open-cart", h);
    return () => window.removeEventListener("4ever:open-cart", h);
  }, [user]);

  const go = (p) => {
    if (p === "account" && !user) { openAuth(); return; }
    const r = parseRoute(`/${p}`);
    setRoute(r);
    const path = routePath(r);
    if (window.location.pathname !== path) {
      window.history.pushState({}, "", path);
    }
    window.scrollTo?.(0, 0);
  };
  const addToCart = (it) => {
    const next = [...items, it];
    setItemsAndSync(next);
    setCartOpen(true);
  };

  // Detect Emergent OAuth callback
  const hash = window.location.hash || "";
  if (hash.includes("session_id=")) {
    return <AuthCallback onDone={() => setRoute({ page: "account", slug: null })} />;
  }

  const pages = {
    home: <HomePage go={go} />,
    product: <ProductPage addToCart={addToCart} go={go} />,
    journal: <JournalPage go={go} />,
    article: <ArticlePage go={go} slug={route.slug} />,
    story: <StoryPage go={go} />,
    faq: <FAQPage />,
    community: <CommunityPage />,
    tracker: <TrackerAppPage />,
    account: <AccountPage go={go} />,
    sizeguide: <SizeGuidePage go={go} />,
    privacy: <PrivacyPage />,
    terms: <TermsPage />,
  };

  return (
    <div style={{ background: C.bg, minHeight: "100vh" }}>
      <AnnouncementBar />
      <Navbar page={page} go={go} cartCount={items.length}
        openCart={() => setCartOpen(true)}
        user={user} onSignIn={openAuth} onSignOut={logout} />
      {loading && !user ? null : (pages[page] || pages.home)}
      <Footer go={go} />
      <CartDrawer open={cartOpen} close={() => setCartOpen(false)}
        items={items} setItems={setItemsAndSync} go={go} />
      <AuthModal />
    </div>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <Shell />
    </AuthProvider>
  );
}
